import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';

// Minimum voice threshold. Effective threshold = max(VAD_RMS, NOISE_MULT × measured noise floor).
const VAD_RMS = environment.sttService.audioChunks.vadRms;
const VAD_WINDOW_SEC = 0.02;         // judge 20 ms windows, not 2.7 ms worklet frames
const NOISE_MULT = 3;                // speech must be ≥3× the noise floor
const NOISE_FLOOR_MAX = 0.05;        // cap so a very loud room can't push the threshold out of reach
const VAD_CALIBRATION_WINDOWS = 25;  // first 0.5 s seeds the floor (min RMS)
const VAD_HYSTERESIS = 0.7;          // once speaking, stay "voiced" down to 70% of the on-threshold
const MIN_RUN_SEC = 0.15;   // voiced time a burst needs before it counts as speech (filters clicks, taps, door slams)
const HANGOVER_SEC = 0.2;   // gaps shorter than this stay inside the same burst (pauses between syllables/words)

/**
 * Energy VAD with an adaptive noise floor. Session-level state (noise floor) lives until reset();
 * chunk-level state (voiced samples, silence timer) is cleared by resetChunk() after every flush.
 */
@Injectable({
    providedIn: 'root'
})
export class VadService {
    // window accumulator
    private sumSq = 0;
    private nativeCount = 0;
    private resampledCount = 0;

    // session state
    private windows = 0;
    private active = false;
    private floor = VAD_RMS / NOISE_MULT;

    // chunk state
    private voicedCount = 0;                  // in resampled (output-rate) samples
    private silenceStart: number | null = null;

    private pendingSec = 0;     // voiced time of a burst not yet confirmed as speech
    private pendingCount = 0;
    private gapSec = 0;         // continuous unvoiced time since the last voiced window
    private runActive = false;  // current burst already confirmed as speech

    /** Voiced samples in the current chunk, at the resampled rate (caller divides by its sample rate). */
    get voicedSamples(): number { return this.voicedCount; }
    get noiseFloor(): number { return this.floor; }
    get threshold(): number { return Math.max(VAD_RMS, this.floor * NOISE_MULT); }

    /** Continuous silence so far in the current chunk, in ms. */
    silenceMs(now = Date.now()): number {
        return this.silenceStart === null ? 0 : now - this.silenceStart;
    }

    /** New session: clear everything, including the learned noise floor. */
    reset(): void {
        this.sumSq = 0;
        this.nativeCount = 0;
        this.resampledCount = 0;
        this.windows = 0;
        this.active = false;
        this.floor = VAD_RMS / NOISE_MULT;
        this.pendingSec = 0;
        this.pendingCount = 0;
        this.gapSec = 0;
        this.runActive = false;
        this.resetChunk();
    }

    /** After a flush: clear chunk counters, keep the noise floor. */
    resetChunk(): void {
        this.voicedCount = 0;
        this.silenceStart = null;
    }

    /**
     * Feed one worklet frame (native rate). `resampledLen` is the length of the same audio
     * after resampling, so voiced time is counted in the caller's output samples.
     */
    push(pcm: Float32Array, resampledLen: number, nativeRate: number, now = Date.now()): void {
        for (let i = 0; i < pcm.length; i++) this.sumSq += pcm[i] * pcm[i];
        this.nativeCount += pcm.length;
        this.resampledCount += resampledLen;
        if (this.nativeCount < nativeRate * VAD_WINDOW_SEC) return;

        const winSec = this.nativeCount / nativeRate;
        const voiced = this.step(Math.sqrt(this.sumSq / this.nativeCount));
        if (voiced) {
            this.gapSec = 0;
            if (this.runActive) {
                this.voicedCount += this.resampledCount;
            } else {
                this.pendingSec += winSec;
                this.pendingCount += this.resampledCount;
                if (this.pendingSec >= MIN_RUN_SEC) { // burst confirmed: count it from its start
                    this.runActive = true;
                    this.voicedCount += this.pendingCount;
                    this.pendingSec = 0;
                    this.pendingCount = 0;
                }
            }
            if (this.runActive) this.silenceStart = null; // unconfirmed blips don't reset the silence timer
        } else {
            this.gapSec += winSec;
            if (this.gapSec > HANGOVER_SEC) { // burst over (or never confirmed): drop it
                this.runActive = false;
                this.pendingSec = 0;
                this.pendingCount = 0;
            }
            if (this.silenceStart === null) this.silenceStart = now;
        }
        this.sumSq = 0;
        this.nativeCount = 0;
        this.resampledCount = 0;
    }

    private step(rms: number): boolean {
        if (this.windows < VAD_CALIBRATION_WINDOWS) {
            this.floor = this.windows === 0 ? rms : Math.min(this.floor, rms);
        }
        this.windows++;
        const on = Math.max(VAD_RMS, this.floor * NOISE_MULT);
        const voiced = rms >= (this.active ? on * VAD_HYSTERESIS : on);
        if (this.windows > VAD_CALIBRATION_WINDOWS) {
            // floor drops fast, rises slowly (faster when not voiced, barely at all during speech)
            this.floor += (rms - this.floor) * (rms < this.floor ? 0.1 : voiced ? 0.001 : 0.05);
        }
        this.floor = Math.min(this.floor, NOISE_FLOOR_MAX);
        this.active = voiced;
        return voiced;
    }
}